import axios from 'axios';
import { BACKEND_URL, API_URL } from '../utils/config';

const API = API_URL;

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Campaign APIs
export const campaignAPI = {
  // Get all campaigns with optional filters
  list: async (params = {}) => {
    const response = await apiClient.get('/campaigns', { params });
    return response.data;
  },

  // Get single campaign by ID
  get: async (id) => {
    const response = await apiClient.get(`/campaigns/${id}`);
    return response.data;
  },

  // Create new campaign
  create: async (campaignData) => {
    const response = await apiClient.post('/campaigns', campaignData);
    return response.data;
  },

  // Update campaign
  update: async (id, campaignData) => {
    const response = await apiClient.put(`/campaigns/${id}`, campaignData);
    return response.data;
  },

  // Delete campaign
  delete: async (id) => {
    const response = await apiClient.delete(`/campaigns/${id}`);
    return response.data;
  },

  // Get user's campaigns
  getMyCampaigns: async (filters = {}) => {
    const response = await apiClient.get('/campaigns', { params: { creator: 'me', ...filters } });
    return response.data;
  },

  // Get user's active campaigns
  getMyActiveCampaigns: async () => {
    const response = await apiClient.get('/campaigns', { params: { creator: 'me', campaign_status: 'active' } });
    return response.data;
  },

  // Get user's draft campaigns
  getMyDrafts: async () => {
    const response = await apiClient.get('/campaigns', { params: { creator: 'me', campaign_status: 'draft' } });
    return response.data;
  },

  // Get user's projects
  getMyProjects: async () => {
    const response = await apiClient.get('/campaigns', { params: { creator: 'me', campaign_type: 'project' } });
    return response.data;
  },

  // Get campaign updates
  getUpdates: async (id) => {
    const response = await apiClient.get(`/campaigns/${id}/updates`);
    return response.data;
  },

  // Post campaign update
  postUpdate: async (id, updateData) => {
    const response = await apiClient.post(`/campaigns/${id}/updates`, updateData);
    return response.data;
  },

  // Edit campaign
  edit: async (id, updateData) => {
    const response = await apiClient.put(`/campaigns/${id}/edit`, updateData);
    return response.data;
  },
};

// Donation APIs
export const donationAPI = {
  // Create donation
  create: async (donationData) => {
    const response = await apiClient.post('/donations', donationData);
    return response.data;
  },

  // Get donations for a campaign
  getByCampaign: async (campaignId, params = {}) => {
    const response = await apiClient.get(`/donations/campaign/${campaignId}`, { params });
    return response.data;
  },

  // Get user's donations
  getMyDonations: async (params = {}) => {
    const response = await apiClient.get('/donations/user', { params });
    return response.data;
  },

  // Get recent donations
  getRecent: async (limit = 10) => {
    const response = await apiClient.get('/donations/recent', { params: { limit } });
    return response.data;
  },
};

// Auth APIs (already in AuthContext, but available here too)
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (name, email, password) => {
    const response = await apiClient.post('/auth/register', { name, email, password });
    return response.data;
  },

  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// AI APIs
export const aiAPI = {
  generateStory: async (campaignData) => {
    const response = await apiClient.post('/chat/generate-story', campaignData);
    return response.data;
  },

  // Social Media Post Generator
  generateSocialPost: async (campaignId, platform, tone = 'inspiring') => {
    const response = await apiClient.post('/ai/social-media-post', {
      campaign_id: campaignId,
      platform,
      tone
    });
    return response.data;
  },

  generateAllSocialPosts: async (campaignId) => {
    const response = await apiClient.post(`/ai/social-media-posts-all?campaign_id=${campaignId}`, {}, { timeout: 60000 }); // 60s timeout for AI generation
    return response.data;
  },

  // Campaign Image Generator
  generateCampaignImage: async (title, category, description = '', style = 'photorealistic') => {
    const response = await apiClient.post('/ai/generate-image', {
      campaign_title: title,
      category,
      description,
      style
    }, { timeout: 120000 }); // 2 min timeout for image generation
    return response.data;
  },

  // Campaign Optimizer
  optimizeCampaign: async (campaignId) => {
    const response = await apiClient.post('/ai/optimize-campaign', {
      campaign_id: campaignId
    });
    return response.data;
  },

  // Thank You Message Generator
  generateThankYouMessage: async (campaignId, donorName, donationAmount, isFirstTimeDonor = true, tone = 'heartfelt') => {
    const response = await apiClient.post('/ai/thank-you-message', {
      campaign_id: campaignId,
      donor_name: donorName,
      donation_amount: donationAmount,
      is_first_time_donor: isFirstTimeDonor,
      tone
    });
    return response.data;
  },

  generateBulkThankYouMessages: async (campaignId) => {
    const response = await apiClient.post(`/ai/thank-you-messages-bulk?campaign_id=${campaignId}`);
    return response.data;
  },

  // Smart Recommendations
  getRecommendations: async (userId = null, interests = [], limit = 6) => {
    const response = await apiClient.post('/ai/recommendations', {
      user_id: userId,
      interests,
      limit
    });
    return response.data;
  },

  getTrendingCampaigns: async (limit = 6) => {
    const response = await apiClient.get(`/ai/recommendations/trending?limit=${limit}`);
    return response.data;
  },

  // Multi-Language Translator
  translateCampaign: async (campaignId, targetLanguage) => {
    const response = await apiClient.post('/ai/translate', {
      campaign_id: campaignId,
      target_language: targetLanguage
    });
    return response.data;
  },

  // Success Predictor
  predictSuccess: async (campaignId) => {
    const response = await apiClient.post(`/ai/predict-success/${campaignId}`);
    return response.data;
  },

  // Trust Score & Fraud Detection
  getTrustScore: async (campaignId) => {
    const response = await apiClient.post(`/ai/trust-score/${campaignId}`);
    return response.data;
  },

  getTrustBadge: async (campaignId) => {
    const response = await apiClient.get(`/ai/trust-badge/${campaignId}`);
    return response.data;
  }
};

// Admin APIs
export const adminAPI = {
  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  getUsers: async (skip = 0, limit = 50, kycStatus = null) => {
    const params = new URLSearchParams({ skip, limit });
    if (kycStatus) params.append('kyc_status', kycStatus);
    const response = await apiClient.get(`/admin/users?${params}`);
    return response.data;
  },

  getUserDetail: async (userId) => {
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response.data;
  },

  getPendingKYC: async () => {
    const response = await apiClient.get('/admin/kyc/pending');
    return response.data;
  },

  makeKYCDecision: async (userId, decision, reason = '') => {
    const response = await apiClient.post(`/admin/kyc/${userId}/decision`, {
      decision,
      reason
    });
    return response.data;
  },

  getPendingCampaigns: async () => {
    const response = await apiClient.get('/admin/campaigns/pending');
    return response.data;
  },

  makeCampaignDecision: async (campaignId, decision, reason = '') => {
    const response = await apiClient.post(`/admin/campaigns/${campaignId}/decision`, {
      decision,
      reason
    });
    return response.data;
  },

  getRecentActivity: async (limit = 20) => {
    const response = await apiClient.get(`/admin/activity?limit=${limit}`);
    return response.data;
  }
  
  // REMOVED: makeAdmin - this endpoint has been removed for security
  // Use scripts/seed_admin.py for admin user creation
};

// Payment APIs
export const paymentAPI = {
  // Create a donation checkout session
  createDonationSession: async (donationData) => {
    const response = await apiClient.post('/payments/create-donation-session', {
      campaign_id: donationData.campaignId,
      amount: donationData.amount,
      tip_amount: donationData.tipAmount || 0,
      currency: donationData.currency || 'usd',
      donor_name: donationData.donorName,
      donor_email: donationData.donorEmail,
      donor_id: donationData.donorId || null,  // Include user ID if logged in
      message: donationData.message || '',
      is_anonymous: donationData.isAnonymous || false,
      origin_url: window.location.origin,
      team_page_id: donationData.teamPageId || null,  // Include team page ID if donating via team page
      referral_code: donationData.referralCode || null  // Include referral code for tracking
    });
    return response.data;
  },

  // Check payment status
  getCheckoutStatus: async (sessionId) => {
    const response = await apiClient.get(`/payments/checkout-status/${sessionId}`);
    return response.data;
  },

  // PayPal: Create order
  createPayPalOrder: async (orderData) => {
    const response = await apiClient.post('/paypal/create-order', orderData);
    return response.data;
  },

  // PayPal: Capture order after approval
  capturePayPalOrder: async (captureData) => {
    const response = await apiClient.post('/paypal/capture-order', captureData);
    return response.data;
  },

  // PayPal: Get config
  getPayPalConfig: async () => {
    const response = await apiClient.get('/paypal/config');
    return response.data;
  }
};

// Stripe Connect APIs (for automatic payouts to creators)
export const stripeConnectAPI = {
  // Check if Stripe Connect is available for this platform (no auth required)
  checkAvailability: async () => {
    const response = await apiClient.get('/connect/availability');
    return response.data;
  },

  // Start creator onboarding - get link to Stripe Express
  startOnboarding: async () => {
    const response = await apiClient.post('/connect/onboard', {
      return_url: `${window.location.origin}/dashboard?tab=settings&stripe=success`,
      refresh_url: `${window.location.origin}/dashboard?tab=settings&stripe=refresh`
    });
    return response.data;
  },

  // Get creator's Stripe Connect status
  getStatus: async () => {
    const response = await apiClient.get('/connect/status');
    return response.data;
  },

  // Get link to Stripe Express dashboard
  getDashboardLink: async () => {
    const response = await apiClient.get('/connect/dashboard-link');
    return response.data;
  },

  // Create a split payment (donation to creator, tip to platform)
  createSplitPayment: async (paymentData) => {
    const response = await apiClient.post('/connect/create-payment', {
      campaign_id: paymentData.campaignId,
      donation_amount: paymentData.donationAmount,
      tip_amount: paymentData.tipAmount,
      currency: paymentData.currency || 'usd',
      donor_name: paymentData.donorName,
      donor_email: paymentData.donorEmail,
      message: paymentData.message || '',
      is_anonymous: paymentData.isAnonymous || false,
      team_page_id: paymentData.teamPageId || null  // Include team page ID for team fundraising
    });
    return response.data;
  },

  // Get payment status for split payment
  getPaymentStatus: async (sessionId) => {
    const response = await apiClient.get(`/connect/payment-status/${sessionId}`);
    return response.data;
  },

  // Get creator earnings
  getEarnings: async () => {
    const response = await apiClient.get('/connect/earnings');
    return response.data;
  },

  // Disconnect Stripe account from platform
  disconnect: async () => {
    const response = await apiClient.post('/connect/disconnect');
    return response.data;
  },

  // Get platform revenue (admin only)
  getPlatformRevenue: async () => {
    const response = await apiClient.get('/connect/platform-revenue');
    return response.data;
  }
};

// Upload APIs (R2 Cloud Storage)
export const uploadAPI = {
  // Upload image to R2
  uploadImage: async (file, folder = 'images') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    const response = await apiClient.post('/r2/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload video to R2 (for small videos <100MB)
  uploadVideo: async (file, campaignId = null, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (campaignId) formData.append('campaign_id', campaignId);
    
    const response = await apiClient.post('/r2/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
    return response.data;
  },

  // Chunked upload for large videos (>100MB)
  uploadLargeVideo: async (file, campaignId = null, onProgress = null) => {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Initialize multipart upload
    const initFormData = new FormData();
    initFormData.append('filename', file.name);
    initFormData.append('content_type', file.type);
    if (campaignId) initFormData.append('campaign_id', campaignId);
    
    const initRes = await apiClient.post('/r2/upload/video/init-multipart', initFormData);
    const { upload_id, key } = initRes.data;
    
    const parts = [];
    
    // Upload chunks
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      const partFormData = new FormData();
      partFormData.append('file', chunk);
      partFormData.append('upload_id', upload_id);
      partFormData.append('key', key);
      partFormData.append('part_number', i + 1);
      
      const partRes = await apiClient.post('/r2/upload/video/part', partFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      parts.push({
        part_number: partRes.data.part_number,
        etag: partRes.data.etag,
      });
      
      if (onProgress) {
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
    }
    
    // Complete upload
    const completeRes = await apiClient.post('/r2/upload/video/complete', {
      upload_id,
      key,
      parts,
    });
    
    return completeRes.data;
  },

  // Upload KYC document
  uploadKYC: async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    
    const response = await apiClient.post('/r2/upload/kyc', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get signed URL for private files
  getSignedUrl: async (key) => {
    const response = await apiClient.get(`/r2/signed-url/${key}`);
    return response.data;
  },

  // Delete file
  deleteFile: async (key) => {
    const response = await apiClient.delete(`/r2/delete/${key}`);
    return response.data;
  },

  // Check R2 health
  checkHealth: async () => {
    const response = await apiClient.get('/r2/health');
    return response.data;
  },
};

export default apiClient;
